import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  concat,
  concatMap,
  EMPTY,
  from,
  map,
  of,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { UserActions } from '../state/user/user.actions';
import { Store } from '@ngrx/store';
import { AuthService } from '../../services/auth.service';
import { UserService } from '../../services/user.service';
import { ACCESS_TOKEN_KEY, LocalStorageService } from '@ui';
import { Router } from '@angular/router';
import { routerSegments } from '../../consts/routes';
import { AuthActions } from '../state/auth/auth.actions';
import { userFeature } from '../state/user/user.feature';

export const setStoreSession = createEffect(
  (actions$ = inject(Actions), authService = inject(AuthService)) => {
    return actions$.pipe(
      ofType(UserActions.setStoreSession),
      switchMap(({ data }) => {
        authService.setSession(data);
        return of(AuthActions.startLoadRequiredData());
      }),
    );
  },
  { functional: true },
);

export const getUserProfile = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    authService = inject(AuthService),
    userService = inject(UserService),
    localStorageService = inject(LocalStorageService),
  ) => {
    return actions$.pipe(
      ofType(UserActions.getUserProfile),
      withLatestFrom(store.select(userFeature.selectProfile)),
      switchMap(([, userProfile]) => {
        // Already loaded -> don't hit backend. Safe to no-op: `loaded` was
        // already set true alongside `profile` by the earlier success.
        if (userProfile) {
          return EMPTY;
        }

        const isLoggedIn = authService.isLoggedIn();
        const accessToken =
          localStorageService.getItemWithCheck(ACCESS_TOKEN_KEY);

        // isLoggedIn() only checks the id token/expiration, not the access
        // token, so it can be true with no access token present. Either way
        // this must resolve to success or fail (never a silent EMPTY) so
        // `loaded` always settles — startLoadRequiredData's combineLatest
        // wait would otherwise hang forever waiting on this flag.
        if (!isLoggedIn || !accessToken) {
          return of(UserActions.getUserProfileFail());
        }

        return userService.getUserProfile(accessToken).pipe(
          map((profile) => UserActions.getUserProfileSuccess({ profile })),
          catchError(() => of(UserActions.getUserProfileFail())),
        );
      }),
    );
  },
  { functional: true },
);

// After a fresh login, wait for the full required-data load (profile,
// settings, org, permissions) to finish before requesting the redirect —
// otherwise navigation races startLoadRequiredData and permissionGuard
// activates against still-empty permissions state.
export const armRedirectAfterLogin = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(UserActions.setStoreSession),
      switchMap(() =>
        actions$.pipe(
          ofType(AuthActions.requiredDataLoadedSuccess),
          take(1),
          map(() => AuthActions.redirectToHome()),
        ),
      ),
    ),
  { functional: true },
);

export const redirectToHome = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) =>
    actions$.pipe(
      ofType(AuthActions.redirectToHome),
      switchMap(() => from(router.navigateByUrl(routerSegments.home))),
    ),
  { functional: true, dispatch: false },
);

export const logout = createEffect(
  (actions$ = inject(Actions), auth = inject(AuthService)) =>
    actions$.pipe(
      ofType(AuthActions.logout),
      concatMap(({ msg }) => {
        auth.logout(msg);
        return concat(of(UserActions.resetToInitialState()));
      }),
    ),
  { functional: true },
);

export const logoutCallback = createEffect(
  (actions$ = inject(Actions), auth = inject(AuthService)) =>
    actions$.pipe(
      ofType(AuthActions.logoutCallback),
      tap(() => auth.handleLogoutCallback(undefined, false)),
    ),
  { functional: true, dispatch: false },
);
